import React, { useState, useEffect } from 'react';
import AppRoutes from '../routes';
import Footer from '../components/Footer';
import AppBar from '../components/AppBar';
import { KleverWalletProvider } from '../context/KleverWalletContext';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import { TermsModal } from '../components';
import MigrationBlocker from '../components/MigrationBlocker/MigrationBlocker';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useSystemNotification } from '../hooks/useSystemNotification';
import { useAppSelector } from '../redux/Store';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Root layout component
 * Wraps the entire application with AppBar, routing, and footer
 */
/** Read persisted theme from localStorage on mount. Must match the
 *  identical read in AppBar so the toggle-button visual state and the
 *  applied <html data-theme> agree on the initial render. Falls back
 *  to dark if localStorage is empty, unreadable (private mode), or
 *  contains a value we don't recognize. */
function initialTheme(): boolean {
  try {
    const stored = localStorage.getItem('klyx-theme');
    if (stored === 'light') return false;
    if (stored === 'dark') return true;
  } catch {
    /* private mode / storage disabled */
  }
  return true;
}

const RootLayout: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(initialTheme);
  const { showWarning, secondsRemaining, handleContinue, handleLogout } = useSessionTimeout();
  useSystemNotification();
  const requiresTermsAccept = useAppSelector(state => state.auth.user?.requiresTermsAccept);

  // Mirror isDarkMode onto <html data-theme> at mount + on every change.
  // The .App wrapper below carries data-theme too, but anything portalled
  // to document.body (PaywallModal, SessionTimeoutModal, TermsModal, toast
  // container) sits OUTSIDE the App wrapper — so without data-theme on
  // <html>, portals fell through to :root (light) even when the app was
  // in dark mode. Issue #378.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleThemeToggle = (isDark: boolean) => {
    setIsDarkMode(isDark);
    // Theme preference is saved by AppBar component. The useEffect above
    // handles propagating the new value to <html data-theme>.
  };

  return (
    <KleverWalletProvider>
      <div className="App" data-theme={isDarkMode ? 'dark' : 'light'}>
        <AppBar onThemeToggle={handleThemeToggle} />
        <div className="app-content">
          <AppRoutes />
        </div>
        <Footer />
        {requiresTermsAccept && <TermsModal />}
        <MigrationBlocker />
        <SessionTimeoutModal
          isOpen={showWarning}
          secondsRemaining={secondsRemaining}
          onContinue={handleContinue}
          onLogout={handleLogout}
        />
        {/* z-index bumped above the shared modal-overlay (10000)
            so toasts triggered inside a modal (e.g. copy-token
            success/failure in the verify-agent modals) render on
            top of the modal shell instead of being visually
            trapped behind it. */}
        <ToastContainer
          position="top-right"
          theme="dark"
          style={{ zIndex: 10050 }}
        />
      </div>
    </KleverWalletProvider>
  );
};

export default RootLayout;
